import React, { useState } from "react";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { Save, ArrowRight, RotateCcw } from "lucide-react";

/**
 * Two-step editor for a security-sensitive account field (email or password).
 * Step 1: user edits the value, optionally types current password.
 *         Clicking "Send code" fires POST /security/otp/send.
 * Step 2: user enters 6-digit SMS code + current password + new value.
 *         POST /security/otp/confirm applies the change on the server.
 *
 * Props:
 *   field         – "email" | "password"  (drives which body params to send)
 *   purpose       – "email_change" | "password_change"
 *   label         – panel heading (e.g. "Email address")
 *   icon          – lucide icon component
 *   currentValue  – value to prefill / show as placeholder text
 *   disabled      – true when phone isn't verified yet
 *   disabledReason– copy explaining why the panel is locked
 *   testid        – base data-testid, we append slugs like `-input`, `-otp` etc.
 *   onSaved       – callback fired after a successful confirm (refresh /auth/me)
 */
export default function SecureFieldPanel({
  field, purpose, label, icon: Icon, currentValue, disabled,
  disabledReason, testid, onSaved,
}) {
  const isEmail = field === "email";
  const [step, setStep] = useState("idle"); // idle | verify
  const [newValue, setNewValue] = useState("");
  const [currentPw, setCurrentPw] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState("");

  const reset = () => {
    setStep("idle"); setNewValue(""); setCurrentPw(""); setCode(""); setSentTo("");
  };

  const sendCode = async (e) => {
    e?.preventDefault?.();
    if (!newValue) return;
    if (isEmail && !/^\S+@\S+\.\S+$/.test(newValue)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (!isEmail && newValue.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (!currentPw) {
      toast.error("Enter your current password");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/security/otp/send", { purpose });
      setSentTo(r.data?.sent_to || "your phone");
      setStep("verify");
      toast.success(`Code sent to ${r.data?.sent_to || "your phone"}`);
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const confirm = async (e) => {
    e?.preventDefault?.();
    if (!code || code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const payload = { purpose, code, current_password: currentPw };
      if (isEmail) payload.new_email = newValue;
      else payload.new_password = newValue;
      await api.post("/security/otp/confirm", payload);
      toast.success(isEmail ? "Email updated" : "Password updated");
      reset();
      onSaved?.();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="surface p-6" data-testid={testid}>
      <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </p>

      {disabled ? (
        <>
          <p className="mt-3 text-sm">{isEmail ? currentValue : "•••••••••"}</p>
          <div className="mt-3 rounded-lg border border-pink-200 bg-pink-50 px-3 py-2 text-xs text-pink-900"
               data-testid={`${testid}-locked`}>
            {disabledReason}
          </div>
        </>
      ) : step === "idle" ? (
        <form onSubmit={sendCode} className="mt-3 grid gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Current</p>
            <p className="text-sm mt-1" data-testid={`${testid}-current`}>{currentValue || "—"}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
              {isEmail ? "New email" : "New password"}
            </p>
            <input
              data-testid={`${testid}-input`}
              type={isEmail ? "email" : "password"}
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder={isEmail ? "you@yourbrand.com" : "At least 6 characters"}
              autoComplete={isEmail ? "email" : "new-password"}
              className="w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
            />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
              Current password
            </p>
            <input
              data-testid={`${testid}-current-pw`}
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              placeholder="Confirm it's really you"
              autoComplete="current-password"
              className="w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
            />
          </div>
          <button type="submit" disabled={busy || !newValue || !currentPw}
            data-testid={`${testid}-send-code`}
            className="btn-primary self-start disabled:opacity-60">
            {busy ? "Sending…" : "Send SMS code"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      ) : (
        <form onSubmit={confirm} className="mt-3 grid gap-3">
          <div className="rounded-md border border-shell-border bg-shell-tint/40 px-3 py-2 text-xs">
            <p className="uppercase tracking-widest font-semibold text-muted-foreground">Code sent</p>
            <p className="mt-0.5">We texted a 6-digit code to <span className="font-mono">{sentTo}</span>. It expires in 5 minutes.</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">6-digit code</p>
            <input
              data-testid={`${testid}-otp`}
              inputMode="numeric"
              maxLength={6}
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="123456"
              className="w-40 rounded-md border border-shell-border bg-white px-3 py-2 text-lg font-mono tracking-widest focus:outline-none focus:border-primary"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button type="submit" disabled={busy || code.length !== 6}
              data-testid={`${testid}-confirm`}
              className="btn-primary disabled:opacity-60">
              <Save className="w-4 h-4" /> {busy ? "Saving…" : `Save new ${field}`}
            </button>
            <button type="button" onClick={reset} data-testid={`${testid}-cancel`}
              className="text-xs font-semibold text-muted-foreground hover:text-foreground border border-shell-border rounded-full px-4 py-2 inline-flex items-center gap-1">
              <RotateCcw className="w-3 h-3" /> Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
